import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Play,
  Pause,
} from "lucide-react";
import MainLayout from "../layout/MainLayout";

const images = Array.from({ length: 26 }, (_, i) => {
  try {
    return require(`../assets/gallery/image-${i + 1}.jpeg`);
  } catch (err) {
    return null;
  }
}).filter(Boolean);

export const Gallery = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [direction, setDirection] = useState(0); // for animation direction

  const selectedImg = selectedIndex !== null ? images[selectedIndex] : null;

  // Lock background scroll
  useEffect(() => {
    document.body.style.overflow = selectedImg ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [selectedImg]);

  // Next / Prev image logic
  const nextImage = useCallback(() => {
    setDirection(1);
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, []);

  const prevImage = useCallback(() => {
    setDirection(-1);
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (selectedIndex === null) return;
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") setSelectedIndex(null);
    },
    [selectedIndex, nextImage, prevImage]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Slideshow autoplay
  useEffect(() => {
    let timer;
    if (isSlideshow && selectedIndex !== null) {
      timer = setInterval(() => {
        nextImage();
      }, 3500);
    }
    return () => clearInterval(timer);
  }, [isSlideshow, selectedIndex, nextImage]);

  // Fullscreen handling
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Image motion variants
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 200 : -200,
      opacity: 0,
      scale: 0.95,
    }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (dir) => ({
      x: dir > 0 ? -200 : 200,
      opacity: 0,
      scale: 0.95,
    }),
  };

  return (
    <MainLayout hideNavbar={!!selectedImg || isFullscreen}>
      {/* Header */}
      <section className="text-center py-16 px-4">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-4 just-me-again-down-here-regular"
        >
          Explore Our Fleet Gallery
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
        >
          Take a closer look at our premium rides — carefully maintained, ready
          for your next adventure.
        </motion.p>
      </section>

      {/* Gallery Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {images.map((img, index) => (
          <motion.div
            key={index}
            className="relative overflow-hidden rounded-xl cursor-pointer group"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.03 }}
            onClick={() => setSelectedIndex(index)}
          >
            <img
              src={img}
              alt={`Fleet ${index + 1}`}
              className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-lg font-medium">
              View Image
            </div>
          </motion.div>
        ))}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[999] backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="relative w-full max-w-6xl max-h-[90vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedIndex(null)}
                className="absolute top-4 right-4 bg-black/60 p-2 rounded-full text-white hover:bg-black transition"
              >
                <X size={24} />
              </button>

              {/* Prev Button */}
              <button
                onClick={prevImage}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 bg-black/60 p-2 rounded-full text-white hover:bg-black transition"
              >
                <ChevronLeft size={28} />
              </button>

              {/* Next Button */}
              <button
                onClick={nextImage}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 bg-black/60 p-2 rounded-full text-white hover:bg-black transition"
              >
                <ChevronRight size={28} />
              </button>

              {/* Scrollable + Animated image */}
              <div className="overflow-auto max-h-[90vh] w-full flex justify-center">
                <AnimatePresence custom={direction} initial={false}>
                  <motion.img
                    key={selectedImg}
                    src={selectedImg}
                    alt="Selected Car"
                    className="rounded-xl max-w-full h-auto object-contain shadow-2xl"
                    variants={variants}
                    custom={direction}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.3 },
                    }}
                  />
                </AnimatePresence>
              </div>

              {/* Toolbar */}
              <div className="absolute bottom-4 flex items-center gap-3 bg-black/50 rounded-full px-4 py-2">
                <button
                  onClick={() => setIsSlideshow((p) => !p)}
                  className="text-white flex items-center gap-1 hover:text-teal-400 transition"
                >
                  {isSlideshow ? (
                    <>
                      <Pause size={18} /> Pause
                    </>
                  ) : (
                    <>
                      <Play size={18} /> Slideshow
                    </>
                  )}
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="text-white flex items-center gap-1 hover:text-teal-400 transition"
                >
                  <Maximize2 size={18} /> Fullscreen
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};
